import React, { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { Button, TextInput } from '@mantine/core';
import { useNavigation } from '../../contexts/NavigationContext';

interface SearchNotesFilterProps {
  value: string;
  onChange: (value: string) => void;
}

export const SearchNotesFilter: React.FC<SearchNotesFilterProps> = ({ value, onChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const { isMobile } = useNavigation();

  const handleToggle = () => {
    if (isExpanded) {
      // If we're collapsing, clear the search and close
      setSearchValue('');
      onChange('');
      setIsExpanded(false);
    } else {
      // If we're expanding, focus the input
      setIsExpanded(true);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setSearchValue(newValue);
    onChange(newValue);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleToggle();
    }
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSearchValue('');
    onChange('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Update internal state when prop changes
  useEffect(() => {
    setSearchValue(value);
    // Expand if there's a value, collapse if value is cleared externally
    if (value) {
      setIsExpanded(true);
    } else if (value === '' && searchValue !== '') {
      // Value was cleared externally, so collapse
      setIsExpanded(false);
    }
  }, [value, searchValue]);

  if (isExpanded) {
    return (
      <div style={{ position: 'relative', width: '200px', flexShrink: 0 }}>
        <TextInput
          ref={inputRef}
          value={searchValue}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          placeholder="Search notes..."
          size="sm"
          leftSection={<Search size={16} />}
          rightSection={
            searchValue ? (
              <X
                size={16}
                onClick={handleClear}
                style={{ cursor: 'pointer', opacity: 0.7 }}
                data-clear-button="true"
              />
            ) : null
          }
          styles={{
            input: {
              fontSize: '14px',
              paddingLeft: '40px',
              paddingRight: searchValue ? '40px' : '16px',
            }
          }}
          onBlur={(e) => {
            // Don't collapse if clicking on the clear button (X)
            if (e.relatedTarget?.closest('[data-clear-button]')) {
              return;
            }
            // Only collapse when losing focus if there's no text in the field
            if (!searchValue.trim()) {
              setTimeout(() => {
                setIsExpanded(false);
              }, 100);
            }
          }}
        />
      </div>
    );
  }

  return (
    <Button
      variant="default"
      size={isMobile ? "compact-sm" : "sm"}
      onClick={handleToggle}
      title="Search notes"
      px={isMobile ? 8 : 12}
      style={{ aspectRatio: '1' }}
    >
      {value ? (
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Search size={isMobile ? 12 : 16} />
          <X 
            size={isMobile ? 12 : 16} 
            onClick={(e) => {
              e.stopPropagation();
              setSearchValue('');
              onChange('');
            }}
            style={{ cursor: 'pointer', opacity: 0.7 }}
          />
        </span>
      ) : (
        <Search size={isMobile ? 12 : 16} />
      )}
    </Button>
  );
};